import { useEffect, useState, useMemo } from 'react';
import { Search, ExternalLink } from 'lucide-react';
import DataTable from '../components/DataTable';
import StyledSelect from '../components/StyledSelect';
import { fetchLeads } from '../api/placeholder';

const LEAD_CATEGORY_OPTIONS = [
  { value: 'All', label: 'All Categories' },
  { value: 'HOT', label: 'HOT' },
  { value: 'WARM', label: 'WARM' },
  { value: 'COLD', label: 'COLD' },
  { value: 'NON-SALES', label: 'NON-SALES' },
];

const MILESTONE_OPTIONS = [
  { value: 'All', label: 'All Milestones' },
  { value: '1', label: 'Milestone 1' },
  { value: '2', label: 'Milestone 2' },
  { value: '3', label: 'Milestone 3' },
  { value: '4', label: 'Milestone 4' },
  { value: '5', label: 'Milestone 5' },
  { value: '6', label: 'Milestone 6' },
  { value: '7', label: 'Milestone 7' },
];

const categoryColors = {
  HOT: 'bg-neon-pink/15 text-neon-pink border border-neon-pink/30',
  WARM: 'bg-neon-amber/15 text-neon-amber border border-neon-amber/30',
  COLD: 'bg-neon-blue/15 text-neon-blue border border-neon-blue/30',
  'NON-SALES': 'bg-surface-600 text-slate-400 border border-surface-500',
};

const riskColors = {
  HIGH: 'text-neon-pink font-semibold',
  MEDIUM: 'text-neon-amber font-semibold',
  LOW: 'text-neon-green font-semibold',
};

function formatCompletedDate(val) {
  if (!val) return null;
  const ts = Number(val);
  if (isNaN(ts)) return val;
  return new Date(ts).toLocaleDateString([], { year: 'numeric', month: 'short', day: 'numeric' });
}

const columns = [
  { key: 'phone', label: 'Phone' },
  {
    key: 'name',
    label: 'Name',
    render: (val) => val || <span className="text-slate-600">—</span>,
  },
  {
    key: 'milestone_stage',
    label: 'Milestone',
    render: (val) =>
      val != null ? (
        `Milestone ${val}`
      ) : (
        <span className="text-slate-600">—</span>
      ),
  },
  {
    key: 'risk_level',
    label: 'Risk Level',
    render: (val) =>
      val ? (
        <span className={riskColors[val] || ''}>{val}</span>
      ) : (
        <span className="text-slate-600">—</span>
      ),
  },
  {
    key: 'urgency',
    label: 'Urgency',
    render: (val) => {
      if (val == null) return <span className="text-slate-600">—</span>;
      const pct = val * 10;
      const barColor =
        val >= 8 ? 'bg-neon-pink' : val >= 5 ? 'bg-neon-amber' : 'bg-neon-green';
      return (
        <div className="flex items-center gap-2">
          <div className="h-1.5 w-16 rounded-full bg-surface-600">
            <div
              className={`h-full rounded-full ${barColor}`}
              style={{ width: `${pct}%` }}
            />
          </div>
          <span className="text-xs font-medium text-slate-400">{val}/10</span>
        </div>
      );
    },
  },
  {
    key: 'recommendation',
    label: 'Recommendation',
    render: (val) => val || <span className="text-slate-600">—</span>,
  },
  {
    key: 'lead_category',
    label: 'Category',
    render: (val) =>
      val ? (
        <span
          className={`inline-block rounded-full px-2.5 py-0.5 text-xs font-semibold ${categoryColors[val] || ''
            }`}
        >
          {val}
        </span>
      ) : (
        <span className="text-slate-600">—</span>
      ),
  },
  {
    key: 'completed_at',
    label: 'Completed',
    render: (val) => {
      const formatted = formatCompletedDate(val);
      return formatted ? (
        <span className="text-neon-green">{formatted}</span>
      ) : (
        <span className="text-slate-600">Pending</span>
      );
    },
  },
];

export default function Leads() {
  const [leads, setLeads] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [categoryFilter, setCategoryFilter] = useState('All');
  const [milestoneFilter, setMilestoneFilter] = useState('All');
  const [searchQuery, setSearchQuery] = useState('');

  useEffect(() => {
    fetchLeads()
      .then(setLeads)
      .catch((err) => setError(err.message))
      .finally(() => setLoading(false));
  }, []);

  const filtered = useMemo(() => {
    return leads.filter((lead) => {
      if (categoryFilter !== 'All' && lead.lead_category !== categoryFilter)
        return false;
      if (
        milestoneFilter !== 'All' &&
        String(lead.milestone_stage) !== milestoneFilter
      )
        return false;
      if (searchQuery) {
        const q = searchQuery.toLowerCase();
        const matchesPhone = lead.phone && lead.phone.toLowerCase().includes(q);
        const matchesName = lead.name && lead.name.toLowerCase().includes(q);
        if (!matchesPhone && !matchesName) return false;
      }
      return true;
    });
  }, [leads, categoryFilter, milestoneFilter, searchQuery]);

  if (loading) {
    return (
      <div className="flex h-[calc(100vh-8rem)] items-center justify-center">
        <div className="h-8 w-8 animate-spin rounded-full border-4 border-neon-cyan border-t-transparent" />
      </div>
    );
  }

  if (error) {
    return (
      <div>
        <div className="mb-6">
          <h1 className="text-2xl font-bold text-slate-100">Leads Management</h1>
          <p className="text-sm text-slate-500">Track and manage financial wellness leads</p>
        </div>
        <div className="rounded-2xl border border-surface-600 bg-surface-800 p-8 text-center">
          <p className="text-sm text-neon-pink">{error}</p>
          <p className="mt-2 text-xs text-slate-500">Make sure the n8n workflow is active and listening.</p>
        </div>
      </div>
    );
  }

  return (
    <div>
      <div className="mb-6 flex items-start justify-between">
        <div>
          <h1 className="text-2xl font-bold text-slate-100">Leads Management</h1>
          <p className="text-sm text-slate-500">
            Track and manage financial wellness leads
          </p>
        </div>
        <a
          href="https://docs.google.com/spreadsheets/d/1xsc93aBjyyq_zsw9SkQpJo1dRhASxQrRIfzvItQlpQg/edit?usp=sharing"
          target="_blank"
          rel="noopener noreferrer"
          className="flex items-center gap-2 rounded-xl border border-neon-cyan/30 bg-neon-cyan/10 px-4 py-2 text-sm font-medium text-neon-cyan transition-all hover:bg-neon-cyan/20 hover:shadow-neon-cyan"
        >
          <ExternalLink size={16} />
          Export
        </a>
      </div>

      {/* Filters */}
      <div className="mb-4 flex flex-wrap items-center gap-3">
        <div className="relative">
          <Search
            size={16}
            className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-500"
          />
          <input
            type="text"
            placeholder="Search by phone or name..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className="rounded-xl border border-surface-600 bg-surface-700 py-2.5 pl-9 pr-3 text-sm text-slate-200 placeholder:text-slate-500 focus:border-neon-cyan/50 focus:outline-none focus:ring-2 focus:ring-neon-cyan/20 transition-all"
          />
        </div>

        <StyledSelect
          options={LEAD_CATEGORY_OPTIONS}
          value={categoryFilter}
          onChange={setCategoryFilter}
          placeholder="All Categories"
        />

        <StyledSelect
          options={MILESTONE_OPTIONS}
          value={milestoneFilter}
          onChange={setMilestoneFilter}
          placeholder="All Milestones"
        />

        <span className="text-xs text-slate-500">
          {filtered.length} of {leads.length} leads
        </span>
      </div>

      <DataTable columns={columns} data={filtered} emptyMessage="No leads match your filters" />
    </div>
  );
}
